import type { FormField } from "@/lib/types/form/form-field";
import { resolveFormFieldPublicId } from "@/lib/forms/static-field-id";
import { formBlockComponents } from "@/lib/forms/block-components";
import { buildFormInputSchemaParams } from "@/lib/forms/form-validation";

export type SubmissionPayloadBlockType = FormField["blockType"];

export type SubmissionPayloadEntry = {
	fieldId: string;
	blockType: SubmissionPayloadBlockType;
	label: string;
	value: unknown;
};

export type FormSubmissionDataRow = {
	field: string;
	value: string;
};

export type SubmissionValidationResult = {
	isValid: boolean;
	errors: Record<string, string>;
};

export function buildFormSubmissionData(fields: FormField[], fieldValues: Record<string, unknown>): FormSubmissionDataRow[] {
	const rows: FormSubmissionDataRow[] = [];

	for (const field of fields) {
		if (!field.id || field.blockType === "form-content") {
			continue;
		}

		const publicId = resolveFormFieldPublicId(field);
		const value = valueToSubmissionString(fieldValues[publicId]);

		if (value.length === 0) {
			continue;
		}

		rows.push({
			field: resolveFieldLabel(field) || field.id,
			value,
		});
	}

	return rows;
}

export function buildFormSubmissionPayload(fields: FormField[], fieldValues: Record<string, unknown>): SubmissionPayloadEntry[] {
	const entries: SubmissionPayloadEntry[] = [];

	for (const field of fields) {
		if (!field.id || field.blockType === "form-content") {
			continue;
		}

		const publicId = resolveFormFieldPublicId(field);
		entries.push({
			fieldId: publicId,
			blockType: field.blockType,
			label: resolveFieldLabel(field) || publicId,
			value: normalizePayloadValue(fieldValues[publicId]),
		});
	}

	return entries;
}

export function validateSubmissionByFields(fields: FormField[], fieldValues: Record<string, unknown>): SubmissionValidationResult {
	const errors: Record<string, string> = {};

	for (const field of fields) {
		if (!field.id || !field.blockType || !(field.blockType in formBlockComponents)) {
			continue;
		}

		const schemaFactory = formBlockComponents[field.blockType as keyof typeof formBlockComponents].zodSchema;
		const schema = schemaFactory(buildFormInputSchemaParams(field));
		const publicId = resolveFormFieldPublicId(field);
		const result = schema.safeParse(fieldValues[publicId]);

		if (!result.success) {
			const message = result.error.issues[0]?.message;
			if (typeof message === "string" && message.length > 0) {
				errors[field.id] = message;
			}
		}
	}

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
	};
}

function resolveFieldLabel(field: FormField): string {
	const content = field.content as { label?: string | null; singleLabel?: string | null } | undefined;
	const rawLabel = content?.label ?? content?.singleLabel ?? "";

	return rawLabel
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function normalizePayloadValue(value: unknown): unknown {
	if (value === null || value === undefined) return null;
	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
		return value;
	}

	if (Array.isArray(value)) {
		if (value.length === 2 && typeof value[0] === "number" && typeof value[1] === "number") {
			return {
				min: value[0],
				max: value[1],
			};
		}

		return {
			items: value.map((item) => normalizeArrayItem(item)),
		};
	}

	if (typeof value === "object") {
		if ("value" in value) {
			const option = value as { value?: unknown };
			return normalizeArrayItem(option.value);
		}
		return JSON.stringify(value);
	}

	return String(value);
}

function normalizeArrayItem(value: unknown): string | number | boolean | null {
	if (value === null || value === undefined) return null;
	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
		return value;
	}

	if (typeof value === "object" && "value" in value) {
		const option = value as { value?: unknown };
		const optionValue = option.value;
		if (typeof optionValue === "string" || typeof optionValue === "number" || typeof optionValue === "boolean") {
			return optionValue;
		}
	}

	return String(value);
}

export function getHoneypotValue(formElement: HTMLFormElement, fieldName: string = "additional_info"): string {
	return (formElement.elements.namedItem(fieldName) as HTMLInputElement | null)?.value ?? "";
}

function valueToSubmissionString(value: unknown): string {
	if (value === null || value === undefined) {
		return "";
	}

	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
		return String(value);
	}

	return JSON.stringify(value);
}
